import React from 'react';
import { Zap, Shield, Sparkles, Truck, Wrench, Heart } from 'lucide-react';
import { Card } from './ui/card';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';

const Features = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  
  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: t('features.energyEfficient'),
      description: t('features.energyEfficientDesc'),
      gradient: 'from-yellow-500/20 to-orange-500/20',
      iconColor: isDark ? '#eab308' : '#f59e0b',
      borderColor: isDark ? '#eab308' : '#f59e0b',
      glowColor: 'rgba(234, 179, 8, 0.4)'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: t('features.warranty'),
      description: t('features.warrantyDesc'),
      gradient: 'from-blue-500/20 to-cyan-500/20',
      iconColor: isDark ? '#3b82f6' : '#0ea5e9',
      borderColor: isDark ? '#3b82f6' : '#0ea5e9',
      glowColor: 'rgba(14, 165, 233, 0.4)'
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: t('features.customDesign'),
      description: t('features.customDesignDesc'),
      gradient: 'from-pink-500/20 to-purple-500/20',
      iconColor: isDark ? '#ec4899' : '#ff1493',
      borderColor: isDark ? '#ec4899' : '#ff1493',
      glowColor: 'rgba(255, 20, 147, 0.4)'
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: t('features.fastProduction'),
      description: t('features.fastProductionDesc'),
      gradient: 'from-green-500/20 to-emerald-500/20',
      iconColor: isDark ? '#22c55e' : '#10b981',
      borderColor: isDark ? '#22c55e' : '#10b981',
      glowColor: 'rgba(16, 185, 129, 0.4)'
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: t('features.installation'),
      description: t('features.installationDesc'),
      gradient: 'from-purple-500/20 to-indigo-500/20',
      iconColor: isDark ? '#a855f7' : '#9333ea',
      borderColor: isDark ? '#a855f7' : '#9333ea',
      glowColor: 'rgba(147, 51, 234, 0.4)'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: t('features.support'),
      description: t('features.supportDesc'),
      gradient: 'from-red-500/20 to-pink-500/20',
      iconColor: isDark ? '#ef4444' : '#f43f5e',
      borderColor: isDark ? '#ef4444' : '#f43f5e',
      glowColor: 'rgba(244, 63, 94, 0.4)'
    }
  ];

  return (
    <div 
      className="py-20 px-4 transition-colors duration-300"
      style={{
        background: isDark 
          ? 'linear-gradient(to bottom, #000000 0%, #0a0a0a 100%)'
          : 'linear-gradient(to bottom, #ffffff 0%, #fefefe 100%)'
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 
            className="text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300"
            style={{ 
              color: isDark ? '#ff00ff' : '#d946ef',
              textShadow: isDark ? '0 0 30px rgba(255, 0, 255, 0.8), 0 0 50px rgba(255, 0, 255, 0.5)' : '0 0 25px rgba(217, 70, 239, 0.6), 0 0 40px rgba(217, 70, 239, 0.4)'
            }}
          >
            {t('features.title')} <span style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>{t('features.titleHighlight')}</span>
          </h2>
          <p 
            className="text-lg max-w-2xl mx-auto transition-colors duration-300"
            style={{ 
              color: isDark ? '#00ffff' : '#0ea5e9',
              textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.6)' : '0 0 10px rgba(14, 165, 233, 0.5)'
            }}
          >
            {t('features.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card 
              key={index}
              className="p-6 transition-all duration-300 hover:scale-105 group"
              style={{
                backgroundColor: isDark ? '#0a0a0a' : '#ffffff',
                borderColor: feature.borderColor,
                borderWidth: '2px',
                boxShadow: isDark ? `0 0 30px ${feature.glowColor}` : `0 0 25px ${feature.glowColor}`
              }}
            >
              <div 
                className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 bg-gradient-to-br ${feature.gradient}`}
                style={{
                  boxShadow: isDark ? `0 0 40px ${feature.glowColor}` : `0 0 35px ${feature.glowColor}`
                }}
              >
                <div style={{ color: feature.iconColor }}>
                  {feature.icon}
                </div>
              </div>
              <h3 
                className="text-xl font-semibold mb-2 transition-colors duration-300"
                style={{ 
                  color: feature.iconColor,
                  textShadow: isDark ? `0 0 15px ${feature.glowColor}` : `0 0 12px ${feature.glowColor}`
                }}
              >
                {feature.title}
              </h3>
              <p 
                className="text-sm leading-relaxed transition-colors duration-300"
                style={{ color: isDark ? '#ffffff' : '#334155' }}
              >
                {feature.description}
              </p>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Card 
            className="p-8 max-w-3xl mx-auto transition-all duration-300"
            style={{
              background: isDark 
                ? 'linear-gradient(135deg, #0a0a0a 0%, #000000 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #fefefe 100%)',
              borderColor: isDark ? '#ff00ff' : '#ff1493',
              borderWidth: '2px',
              boxShadow: isDark ? '0 0 40px rgba(255, 0, 255, 0.3)' : '0 0 35px rgba(255, 20, 147, 0.4)'
            }}
          >
            <h3 
              className="text-2xl font-bold mb-4 transition-colors duration-300"
              style={{ 
                color: isDark ? '#00ffff' : '#0ea5e9',
                textShadow: isDark ? '0 0 20px rgba(0, 255, 255, 0.8)' : '0 0 18px rgba(14, 165, 233, 0.6)'
              }}
            >
              {t('features.ctaTitle')}
            </h3>
            <p 
              className="mb-6 transition-colors duration-300"
              style={{ 
                color: isDark ? '#ffffff' : '#64748b'
              }}
            >
              {t('features.ctaSubtitle')}
            </p>
            <button 
              onClick={() => {
                const customizerSection = document.getElementById('customizer');
                if (customizerSection) customizerSection.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-3 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                  : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
                boxShadow: isDark 
                  ? '0 10px 50px rgba(255, 0, 255, 0.6), 0 0 35px rgba(255, 0, 255, 0.4)'
                  : '0 10px 45px rgba(255, 20, 147, 0.6), 0 0 30px rgba(255, 0, 128, 0.5)'
              }}
            >
              {t('features.ctaButton')}
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Features;
